import React, {useCallback, useState, useRef, ReactNode, useEffect, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {CheckCircle2} from 'lucide-react';
import {OFFER_CONFIRMATION_TYPES,getFeatureFlags} from '@chekin/core';
import {useCardSwipe, useFetchOffers, useFetchTopOffers} from 'hooks';
import {useReservation} from 'context/reservation';
import {usePostHogTrack, POSTHOG_EVENTS} from 'analytics';
import {shuffleArray} from 'utils/common';
import {DealCardWithCart} from 'components/upselling/EnhanceYourStay/DealCardWithCart';
import {SwipeTutorial} from 'components/upselling/EnhanceYourStay/SwipeTutorial';
import {
  ProgressIndicator,
  ProgressDot,
} from 'components/upselling/EnhanceYourStay/styled';
import {DealsLoadingState} from './DealsLoadingState';
import {DealsEmptyState} from './DealsEmptyState';
import {
  DealsContainer,
  DealsStatusBanner,
  SpinningLoader,
  DealsHeader,
  DealsTitle,
  DealsSubtitle,
  StackWrapper,
  CompactCardStack,
  SkipAllButton,
} from './styled';

const SWIPE_THRESHOLD = 100;
const ROTATION_FACTOR = 0.1;
const EXIT_ANIMATION_DURATION = 300;
const MAX_FALLBACK_OFFERS = 5;

type Props = {
  isRegistrationComplete: boolean;
  registrationMessage?: string | ReactNode;
  onFinishProcess?: () => void;
  onNavigateToCart?: () => void;
};

export function DealsProcessingView({
  isRegistrationComplete,
  registrationMessage,
  onFinishProcess,
  onNavigateToCart,
}: Props) {
  const {t} = useTranslation();
  const {data: reservation} = useReservation();
  const {trackUpsellingEvent, trackEvent} = usePostHogTrack();
  const isTopOffersEnabled = getFeatureFlags('UPSELLING_TOP_OFFERS');
  const {data: topOffersList, isLoading: isTopOffersLoading} = useFetchTopOffers({
    reservationId: reservation?.id,
    enabled: isTopOffersEnabled,
  });
  const {data: paginatedOfferList, isLoading: isOffersLoading} = useFetchOffers({
    housingId: reservation?.housing_id,
  });

  const [showTutorial, setShowTutorial] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [exitingCard, setExitingCard] = useState<{
    index: number;
    direction: 'left' | 'right';
  } | null>(null);

  const autoApprovedDealsCount = useRef(0);
  const exitAnimationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasTrackedOffersShown = useRef(false);

  const offers = useMemo(() => {
    if (isTopOffersEnabled) {
      return topOffersList ?? [];
    }
    return shuffleArray(paginatedOfferList?.results ?? []).slice(0, MAX_FALLBACK_OFFERS);
  }, [isTopOffersEnabled, topOffersList, paginatedOfferList?.results]);

  const isLoading = isTopOffersEnabled ? isTopOffersLoading : isOffersLoading;

  useEffect(() => {
    return () => {
      if (exitAnimationTimeoutRef.current) {
        clearTimeout(exitAnimationTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (offers.length > 0 && !isLoading && !hasTrackedOffersShown.current) {
      hasTrackedOffersShown.current = true;
      trackEvent(POSTHOG_EVENTS.TOP_OFFERS_SHOWN, {
        offers_count: offers.length,
        offer_ids: offers.map(o => o.id),
      });
    }
  }, [offers, isLoading, trackEvent]);

  const advanceToNextCard = useCallback(
    (direction: 'left' | 'right') => {
      setExitingCard({index: currentIndex, direction});
      exitAnimationTimeoutRef.current = setTimeout(() => {
        setCurrentIndex(prev => prev + 1);
        setExitingCard(null);
      }, EXIT_ANIMATION_DURATION);
    },
    [currentIndex],
  );

  const handleSkipCard = useCallback(() => {
    advanceToNextCard('left');
    trackUpsellingEvent({
      event: POSTHOG_EVENTS.OFFER_SKIPPED,
      offer: offers[currentIndex],
    });
  }, [advanceToNextCard, offers, currentIndex, trackUpsellingEvent]);

  const {
    isDragging,
    translateX,
    rotation,
    handleDragStart: baseDragStart,
    handleDragMove,
    handleDragEnd: baseDragEnd,
    resetPosition,
  } = useCardSwipe({
    swipeThreshold: SWIPE_THRESHOLD,
    rotationFactor: ROTATION_FACTOR,
    onSwipeLeft: handleSkipCard,
    disabled: showTutorial,
  });

  const handleTutorialDismiss = useCallback(() => {
    setShowTutorial(false);
  }, []);

  const handleDragStart = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      if (showTutorial) {
        handleTutorialDismiss();
        return;
      }
      baseDragStart(e);
    },
    [showTutorial, handleTutorialDismiss, baseDragStart],
  );

  const handleDragEnd = useCallback(() => {
    baseDragEnd();
    resetPosition();
  }, [baseDragEnd, resetPosition]);

  const handleAddedToCart = useCallback(() => {
    const currentOffer = offers[currentIndex];
    if (currentOffer?.confirmation_type === OFFER_CONFIRMATION_TYPES.auto) {
      autoApprovedDealsCount.current += 1;
    }
    advanceToNextCard('right');
    trackUpsellingEvent({
      event: POSTHOG_EVENTS.OFFER_ADDED,
      offer: currentOffer,
    });
  }, [currentIndex, offers, advanceToNextCard, trackUpsellingEvent]);

  const handleSkipAll = useCallback(() => {
    if (!isRegistrationComplete) {
      return;
    }

    const skippedCount = Math.max(offers.length - currentIndex, 0);
    trackEvent(POSTHOG_EVENTS.ALL_OFFERS_SKIPPED, {
      total_offers: offers.length,
      skipped_count: skippedCount,
    });

    if (autoApprovedDealsCount.current > 0 && onNavigateToCart) {
      onNavigateToCart();
    } else if (onFinishProcess) {
      onFinishProcess();
    }
  }, [
    onFinishProcess,
    onNavigateToCart,
    isRegistrationComplete,
    trackEvent,
    offers.length,
    currentIndex,
  ]);

  const visibleOffers = offers.slice(currentIndex, currentIndex + 4);
  const allDealsCompleted = currentIndex >= offers.length;
  const remainingDeals = Math.max(offers.length - currentIndex, 0);

  useEffect(() => {
    if (allDealsCompleted && isRegistrationComplete) {
      if (autoApprovedDealsCount.current > 0 && onNavigateToCart) {
        onNavigateToCart();
      } else if (onFinishProcess) {
        onFinishProcess();
      }
    }
  }, [allDealsCompleted, isRegistrationComplete, onFinishProcess, onNavigateToCart]);

  if (isLoading) {
    return <DealsLoadingState registrationMessage={registrationMessage} />;
  }

  if (offers.length === 0) {
    return (
      <DealsEmptyState
        isRegistrationComplete={isRegistrationComplete}
        registrationMessage={registrationMessage}
        onFinishProcess={onFinishProcess}
      />
    );
  }

  return (
    <DealsContainer>
      <DealsStatusBanner $isComplete={isRegistrationComplete}>
        {isRegistrationComplete ? (
          <CheckCircle2 size={16} />
        ) : (
          <SpinningLoader size={16} />
        )}
        <span>{registrationMessage}</span>
      </DealsStatusBanner>

      <DealsHeader>
        <DealsTitle className="uppercase">{t('deals_processing')}</DealsTitle>
        <DealsTitle>{t('enhance_your_stay.title')}</DealsTitle>
        {!allDealsCompleted && (
          <DealsSubtitle>
            {t('enhance_your_stay.subtitle', {count: remainingDeals})}
          </DealsSubtitle>
        )}
      </DealsHeader>

      {!allDealsCompleted && (
        <StackWrapper>
          <ProgressIndicator>
            {offers.map((_, index) => (
              <ProgressDot
                key={index}
                $active={index === currentIndex}
                $completed={index < currentIndex}
              />
            ))}
          </ProgressIndicator>

          <CompactCardStack>
            {visibleOffers
              .map((offer, stackIndex) => {
                const actualIndex = currentIndex + stackIndex;
                const isExiting = exitingCard?.index === actualIndex;

                return (
                  <DealCardWithCart
                    key={offer.id}
                    offer={offer}
                    stackIndex={stackIndex}
                    isDragging={isDragging && stackIndex === 0}
                    translateX={stackIndex === 0 ? translateX : 0}
                    rotation={stackIndex === 0 ? rotation : 0}
                    isExiting={isExiting}
                    exitDirection={isExiting ? exitingCard.direction : null}
                    onSkip={handleSkipCard}
                    onAddedToCart={handleAddedToCart}
                    onDragStart={handleDragStart}
                    onDragMove={handleDragMove}
                    onDragEnd={handleDragEnd}
                  />
                );
              })
              .reverse()}

            {showTutorial && currentIndex === 0 && (
              <SwipeTutorial onDismiss={handleTutorialDismiss} />
            )}
          </CompactCardStack>
        </StackWrapper>
      )}

      {isRegistrationComplete && (
        <SkipAllButton
          link
          label={t('enhance_your_stay.skip_all')}
          onClick={handleSkipAll}
        />
      )}
    </DealsContainer>
  );
}
